import React, { useContext, useState, useRef, useEffect } from "react";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import { BodyText, Button, Heading, ChevronLeftIcon, ChevronRightIcon, Space, theme } from "@src/components/nessie-web";
import { Link } from "gatsby";
import { getRelativePath } from "@src/utils/routes";
import { logEvent } from "@src/utils/logClient";
import * as S from "./styles";
import { css } from "@emotion/react";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import Masonry from "react-responsive-masonry";

interface ActivitiesSectionProps {
  activeTag?: string;
  handleQueryParams: (slug: string) => void;
  tags: {
    id: string;
    slug: string;
  }[];
  activities: {
    id: string;
    title: string;
    description: string;
    previewImage: { file: { childImageSharp: { gatsbyImageData: IGatsbyImageData } } };
    slug: string;
    backgroundColor: string;
    tags: {
      slug: string;
    }[];
  }[];
}

const ActivitiesSection: React.FC<ActivitiesSectionProps> = (props) => {
  const { translate: t } = useContext(TranslationContext);

  const [activeTag, setActiveTag] = useState("");
  const [isAtLeft, setIsAtLeft] = useState(true);
  const [isAtRight, setIsAtRight] = useState(false);

  const clearFilters = () => {
    props.handleQueryParams("");
    setActiveTag("");
  };
  const handleTagSelection = (slug: string) => {
    if (activeTag === slug) {
      clearFilters();
    } else {
      setActiveTag(slug);
      props.handleQueryParams(slug);
    }
    logEvent({
      eventName: `web.external.activity_corner.homepage-filter`,
      eventValue: t(`directus.dojo_inspo_tags_${slug}.label`).toString().toLowerCase().replace(" ", "-"),
    });
  };

  const filteredActivities = props.activities.filter(({ tags }) =>
    activeTag ? tags.map(({ slug }) => slug).indexOf(activeTag) > -1 : true,
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollFilterContainer = (scrollAmount: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };
  const handleScroll = () => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      setIsAtLeft(container.scrollLeft === 0);
      setIsAtRight(Math.ceil(container.scrollLeft) + container.clientWidth >= container.scrollWidth);
    }
  };

  useEffect(() => {
    if (props.activeTag) {
      setActiveTag(props.activeTag);
    }
  }, [props.activeTag]);

  return (
    <>
      <S.FilterContainer ref={scrollRef} onScroll={handleScroll}>
        <S.LeftScrollButton className={!isAtLeft ? "active" : ""}>
          <Button
            css={css({ width: 44 })}
            size="s"
            icon={<ChevronLeftIcon />}
            onClick={() => scrollFilterContainer(-100)}
            disabled={isAtLeft}
          />
        </S.LeftScrollButton>
        <Button key="view-all" onClick={clearFilters} kind={"" === activeTag ? "primary" : "secondary"} size="s">
          {t("dojo_inspo.clear_filters")}
        </Button>
        {props.tags.map(({ id, slug }, index) => (
          <Button
            key={index}
            onClick={() => handleTagSelection(slug)}
            kind={slug === activeTag ? "primary" : "secondary"}
            size="s"
          >
            {t(`directus.dojo_inspo_tags_${id}.label`)}
            {slug === "new" && <S.NewTagBadge></S.NewTagBadge>}
          </Button>
        ))}
        <S.RightScrollButton className={!isAtRight ? "active" : ""}>
          <Button
            size="s"
            icon={<ChevronRightIcon />}
            css={css({ width: 44 })}
            onClick={() => scrollFilterContainer(100)}
            disabled={isAtRight}
          />
        </S.RightScrollButton>
      </S.FilterContainer>
      <S.ActivityColumns columnsCountBreakPoints={{ 768: 1, 992: 3 }}>
        <Masonry gutter={`${theme.space.l}px`}>
          {filteredActivities.map((activity, index) => {
            return (
              <Link
                to={getRelativePath(`/activity-corner/${activity.slug}`)}
                onClick={() => {
                  logEvent({
                    activityName: `web.external.activity_corner.card.touch`,
                    activityValue: t(activity.title).toString().toLowerCase().replace(" ", "_"),
                  });
                }}
                key={activity.slug}
                css={css({ breakInside: "avoid-column" })}
              >
                <S.ActivityCard key={index} backgroundColor={activity.backgroundColor}>
                  <GatsbyImage
                    image={activity.previewImage.file.childImageSharp.gatsbyImageData}
                    alt="acitivity preview"
                  />
                  <S.ActivityCardInner>
                    <Heading>{t(activity.title)}</Heading>
                    <Space size="xs" />
                    <BodyText color="taro60" className="description">
                      {t(activity.description)}
                    </BodyText>
                  </S.ActivityCardInner>
                  {activity.tags.findIndex(({ slug }) => slug === "new") > -1 && (
                    <S.NewActivityBadge size="m">New</S.NewActivityBadge>
                  )}
                </S.ActivityCard>
              </Link>
            );
          })}
        </Masonry>
      </S.ActivityColumns>
    </>
  );
};

export default ActivitiesSection;
